import React, { useState } from 'react';
import { Card, Form, Tag } from 'antd';
import { toLower } from 'lodash';

import { useTranslation } from 'react-i18next';
import ButtonComponent from '@/components/Button';
import InputComponent from '@/components/Input';
import SelectInput from '@/components/Select';
import TableComponent from '@/components/Table';
import { PAGE_SIZE, SENDER_CONSTANT } from '@/constants';
import ModalComponent from '@/components/Modal';
import { API } from '@/constants/api';
import { SmsManagementService } from '@/services';
import { ReactComponent as SearchIcon } from '@/assets/images/icons/search.svg';
import { ReactComponent as PlusIcon } from '@/assets/images/icons/plus.svg';
import EllipsisTooltip from '@/components/EllipsisTooltip';
import NewSenderNameForm from './NewSenderNameForm';

import './sender.scss';

const colSize = 100 / 5;

function SenderContainer() {
  const [filterForm] = Form.useForm();
  const [filterParams, setFilterParams] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [triggerRefreshTable, setTriggerRefreshTable] = useState(false);
  const { t } = useTranslation();
  const [needFetchSender, setNeedFetchSender] = useState(false);

  const columns = [
    {
      title: t('sms_page.tabs.third'),
      dataIndex: 'senderName',
      key: 'senderName',
      width: `${colSize}%`,
      ellipsis: {
        showTitle: false,
      },
      render: (_, { senderName }) => (
        <div className="fw-600">
          <EllipsisTooltip title={senderName}>{senderName}</EllipsisTooltip>
        </div>
      ),
    },
    {
      title: t('sms_sender_list.company_detail'),
      dataIndex: 'companyDetail',
      key: 'companyDetail',
      width: `${colSize}%`,
      ellipsis: {
        showTitle: false,
      },
      render: (_, { websiteLink, registrationDocument }) => {
        if (websiteLink) {
          return (
            <EllipsisTooltip title={websiteLink}>
              <a
                href={websiteLink}
                target="_blank"
                rel="noreferrer"
                style={{ color: '#722ED1' }}
              >
                {websiteLink}
              </a>
            </EllipsisTooltip>
          );
        }

        if (registrationDocument) {
          const filename = new URL(registrationDocument).pathname
            .split('/')
            .pop();
          return (
            <EllipsisTooltip title={filename}>
              <a
                href={registrationDocument}
                target="_blank"
                rel="noreferrer"
                style={{ color: '#722ED1' }}
              >
                {filename}
              </a>
            </EllipsisTooltip>
          );
        }

        return null;
      },
    },
    {
      title: t('sms_add_new_sender.sms_use_cases'),
      dataIndex: 'description',
      key: 'description',
      width: `${colSize}%`,
      ellipsis: {
        showTitle: false,
      },
      render: (_, { description }) => (
        <EllipsisTooltip title={description}>{description}</EllipsisTooltip>
      ),
    },
    {
      title: t('sms_sender_list.sender_type'),
      dataIndex: 'useType',
      key: 'useType',
      width: `${colSize}%`,
      render: (_, { useType }) => {
        const senderTypeText = SENDER_CONSTANT.USE_TYPES.find(
          (item) => item.value === useType,
        )?.label;

        return (
          <Tag
            key={senderTypeText}
            className={`sms-tag sms-sender sms-sender-type-${toLower(
              senderTypeText,
            )}`}
          >
            <span>
              {t(`common.constants.sender_constant.use_type.${senderTypeText}`)}
            </span>
          </Tag>
        );
      },
    },
    {
      title: t('common.transactions_table.form_filter.status.label'),
      dataIndex: 'status',
      key: 'status',
      width: `${colSize}%`,
      render: (_, { status }) => {
        const statusText = SENDER_CONSTANT.STATUSES.find(
          (item) => item.value === status,
        )?.label;
        return (
          <Tag
            key={statusText}
            className={`sms-tag sms-sender-${toLower(statusText)}`}
          >
            <span>
              {t(`common.constants.sender_constant.statuses.${statusText}`)}
            </span>
          </Tag>
        );
      },
    },
  ];
  const handleOnFormFilterFinish = (values) => {
    setTriggerRefreshTable(true);
    setFilterParams({
      ...filterParams,
      ...values,
      pagination: {
        current: 1,
        pageSize: PAGE_SIZE,
      },
    });
    setTimeout(() => {
      setTriggerRefreshTable(false);
    }, 50);
  };

  const smsStatusOptions = SENDER_CONSTANT.STATUSES.map((status) => ({
    label: t(`common.constants.sender_constant.statuses_lower.${status.label}`),
    value: status.value,
  }));

  const useTypeOptions = SENDER_CONSTANT.USE_TYPES.map((status) => ({
    label: t(`common.constants.sender_constant.use_type_lower.${status.label}`),
    value: status.value,
  }));

  const handleResetFilterForm = () => {
    filterForm.resetFields();
    setFilterParams({});
  };

  const handleCreateSender = async (values) => {
    delete values.evidence;
    const resp = await SmsManagementService.createSender(values);

    if (resp.success) {
      setNeedFetchSender(true);
      setModalOpen(false);
    }
  };

  const handleModalClose = () => {
    if (needFetchSender) {
      setTriggerRefreshTable(true);
    }
    setNeedFetchSender(false);
  };

  const handleOnClickAddNew = () => {
    setTriggerRefreshTable(false);
    setModalOpen(true);
  };

  return (
    <div className="sender-container">
      <Card
        title={t('sms_add_new_sender.sender_name')}
        bordered={false}
        extra={
          <ButtonComponent
            type="primary"
            icon={<PlusIcon />}
            className="btn-add-new"
            onClick={handleOnClickAddNew}
          >
            {t('sms_sender_list.add_new')}
          </ButtonComponent>
        }
      >
        <div className="form-filter">
          <Form
            name="filter"
            layout="vertical"
            onFinish={handleOnFormFilterFinish}
            autoComplete="off"
            className="form"
            form={filterForm}
          >
            <Form.Item
              label={t('sms_add_new_sender.sender_name')}
              name="senderName"
              className="form-item"
            >
              <InputComponent
                placeholder={t('sms_add_new_sender.sender_name')}
              />
            </Form.Item>

            <Form.Item
              label={t('common.transactions_table.form_filter.status.label')}
              name="status"
              className="form-item"
            >
              <SelectInput
                allowClear
                placeholder={t(
                  'common.transactions_table.form_filter.status.label',
                )}
                options={smsStatusOptions}
              />
            </Form.Item>

            <Form.Item
              label={t('sms_sender_list.sender_type')}
              name="useType"
              className="form-item"
            >
              <SelectInput
                allowClear
                placeholder={t('sms_sender_list.sender_type')}
                options={useTypeOptions}
              />
            </Form.Item>
            <Form.Item>
              <div className="form-actions-item">
                <ButtonComponent
                  type="primary"
                  htmlType="submit"
                  icon={<SearchIcon />}
                >
                  {t('transactions_page_trans.search')}
                </ButtonComponent>
                <ButtonComponent type="default" onClick={handleResetFilterForm}>
                  {t('transactions_page_trans.reset')}
                </ButtonComponent>
              </div>
            </Form.Item>
          </Form>
        </div>

        <TableComponent
          triggerRefresh={triggerRefreshTable}
          columns={columns}
          filterParams={filterParams}
          action={API.SMS_MANAGERMENT.SENDER.INDEX}
        />
      </Card>

      <ModalComponent
        title={t('sms_add_new_sender.new_sender_name')}
        centered
        open={modalOpen}
        footer={null}
        onCancel={() => setModalOpen(false)}
        afterClose={handleModalClose}
      >
        <NewSenderNameForm onSubmit={handleCreateSender} />
      </ModalComponent>
    </div>
  );
}

export default SenderContainer;
